<template>
  <div style="text-align: center;">
    <div v-if="loading">
      <v-progress-linear indeterminate color="primary" />
    </div>
    <div
      v-else-if="!data.items.length"
      style="background-color: #D1DAD9; border-radius: 5px;"
      class="d-flex justify-center align-center"
    >
      {{ emptyText || 'Ei sisältöä' }}
    </div>
    <div
      class="d-flex justify-center"
      v-if="!loading && data.items.length"
      style="color: grey; font-style: italic;"
    >
      {{ subtitle }}
    </div>
    <div v-if="!loading && data.items.length" class="table-wrapper" :style="{ width: totalWidth + 'px' }">
      <!-- Fixed/locked columns container -->
      <div class="fixed-columns" :style="`width: ${totalFixedWidth}px;`">
        <TableColumn
          v-for="(header, index) in fixedHeaders"
          :key="'fixed-' + index"
          :header="header"
          :items="data.items"
          @rowClicked="$emit('rowClicked', $event)"
          class="fixed-column"
          :style="`width: ${parseInt(header.width) || 100}px; min-width: ${parseInt(header.width) || 100}px; max-width: ${parseInt(header.width) || 100}px;`"
        ></TableColumn>
      </div>

      <!-- Scrollable columns container -->
      <div class="scrollable-columns">
        <div class="scrollable-inner" :style="{ width: totalScrollWidth + 'px' }">
          <TableColumn
            v-for="(header, index) in scrollableHeaders"
            :key="'scroll-' + index"
            @rowClicked="$emit('rowClicked', $event)"
            :header="header"
            :items="data.items"
            :style="`width: ${parseInt(header.width) || 100}px; min-width: ${parseInt(header.width) || 100}px;`"
          ></TableColumn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TableColumn from './TableColumn.vue';

export default {
  props: ['data', 'loading', 'emptyText', 'subtitle'],
  components: {
    TableColumn
  },
  computed: {
    // Find the index of the last locked column
    lastLockedIndex() {
      if (!this.data || !this.data.headers) return -1;

      // Find the last header with locked: true
      for (let i = this.data.headers.length - 1; i >= 0; i--) {
        if (this.data.headers[i].locked) {
          return i;
        }
      }
      return -1;
    },

    // Headers for fixed columns
    fixedHeaders() {
      if (this.lastLockedIndex === -1) return [];
      return this.data.headers.slice(0, this.lastLockedIndex + 1);
    },

    // Headers for scrollable columns
    scrollableHeaders() {
      if (this.lastLockedIndex === -1) return this.data.headers;
      return this.data.headers.slice(this.lastLockedIndex + 1);
    },

    // Calculate total width of fixed columns
    totalFixedWidth() {
      return this.fixedHeaders.reduce((total, header) => {
        const width = parseInt(header.width) || 100;
        return total + width;
      }, 0);
    },

    // Calculate total width of scrollable columns
    totalScrollWidth() {
      return this.scrollableHeaders.reduce((total, header) => {
        const width = parseInt(header.width) || 100;
        return total + width;
      }, 0);
    },

    // Calculate total width of all columns
    totalWidth() {
      // Calculate based on all headers, or use the sum of fixed + scrollable if there are locked columns
      if (this.lastLockedIndex === -1) {
        return this.data.headers.reduce((total, header) => {
          const width = parseInt(header.width) || 100;
          return total + width;
        }, 0);
      } else {
        return this.totalFixedWidth + this.totalScrollWidth;
      }
    }
  }
}
</script>

<style scoped>
.table-wrapper {
  position: relative;
  border: 1px solid #eee;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto; /* Center the table */
  max-width: 100%; /* Prevent overflow on small screens */
}

.fixed-columns {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  background-color: white;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-shrink: 0;
}

.scrollable-columns {
  overflow-x: auto;
  width: 100%;
  padding-left: v-bind(totalFixedWidth + 'px');
  box-sizing: border-box;
}

.scrollable-inner {
  display: flex;
  flex-shrink: 0;
}

/* Remove bottom border from last cell of each column */
:deep(.table-column .data-cell:last-child) {
  border-bottom: none;
}

.fixed-column {
  flex-shrink: 0;
}
</style>